import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Card, CardContent } from '../components/ui/card';
import { BellRing, Plus, Pencil, Trash2, ChevronDown, CalendarRange, ShieldCheck, Megaphone, Paperclip } from 'lucide-react';
import { confirmDialog } from '../components/ConfirmHost';
import { UserAnnouncementFormDialog } from '../components/settings/UserAnnouncementFormDialog';
import { AnnouncementTracking } from '../components/settings/AnnouncementTracking';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errText = (e) => (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : e.response?.data?.detail?.[0]?.msg?.replace('Value error, ', '') || 'İşlem başarısız');
const fmt = (d) => (d ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : null);
const strip = ({ id, counts, created_at, created_by, updated_at, attachments, ...rest }) => rest;
const names = (ids, list) => (ids.length ? ids.map(i => list.find(x => x.id === i)?.name).filter(Boolean).join(', ') : 'Tümü');

const Item = ({ a, statuses, locations, onEdit, onDelete, onToggle }) => {
  const [open, setOpen] = useState(false);
  const c = a.counts || {};
  return (
    <Card className={`border-slate-200 ${a.is_active ? '' : 'opacity-60'}`} data-testid={`ua-card-${a.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          {a.mode === 'ack' ? <ShieldCheck className="h-5 w-5 mt-0.5 text-rose-600" /> : <Megaphone className="h-5 w-5 mt-0.5 text-sky-600" />}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-900">{a.title}</p>
            <div className="mt-1 flex flex-wrap gap-1.5 text-xs">
              <Badge variant="outline">{a.mode === 'ack' ? 'Okudum, anladım onaylı' : 'Tek gösterimlik'}</Badge>
              {a.mode === 'ack' && a.block_permits && <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100" data-testid={`ua-block-badge-${a.id}`}>Onaylanana kadar talep engelli</Badge>}
              <Badge variant="secondary" className="font-normal">Statü: {names(a.status_ids, statuses)}</Badge>
              <Badge variant="secondary" className="font-normal">Lokasyon: {names(a.location_ids, locations)}</Badge>
              <span className="flex items-center gap-1 text-slate-500"><CalendarRange className="h-3.5 w-3.5" />{fmt(a.start_date) || 'Hemen'} – {fmt(a.end_date) || 'Süresiz'}</span>
            </div>
          </div>
          <Switch checked={a.is_active} onCheckedChange={(v) => onToggle(a, v)} data-testid={`ua-toggle-${a.id}`} />
          <Button size="icon" variant="ghost" className="text-blue-600" onClick={() => onEdit(a)} data-testid={`ua-edit-${a.id}`}><Pencil className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => onDelete(a)} data-testid={`ua-delete-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
        <div className="announcement-content rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700 max-h-28 overflow-hidden" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(a.html) }} />
        {a.attachments?.length > 0 && <p className="flex items-center gap-1.5 text-xs text-slate-500" data-testid={`ua-att-count-${a.id}`}><Paperclip className="h-3.5 w-3.5" />{a.attachments.map(x => x.name).join(', ')}</p>}
        <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-sm hover:bg-slate-50 transition-colors" data-testid={`ua-track-toggle-${a.id}`}>
          <span className="font-medium text-slate-800">Firma takibi</span>
          {a.mode === 'ack' && <span className="text-emerald-700">{c.acked || 0} okudu ve anladı</span>}
          <span className="text-sky-700">{c.seen || 0} gördü</span>
          <span className="text-rose-700">{c.unseen || 0} henüz görmedi</span>
          <ChevronDown className={`ml-auto h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && <AnnouncementTracking a={a} />}
      </CardContent>
    </Card>
  );
};

export default function UserAnnouncementsPage() {
  const { user, canView, canTab } = useAuth();
  const [items, setItems] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => axios.get(`${API}/user-announcements`).then(r => setItems(r.data)).catch(() => toast.error('Duyurular yüklenemedi'));
  useEffect(() => {
    if (!canView('user_announcements')) return;
    load();
    axios.get(`${API}/company-statuses`).then(r => setStatuses(r.data)).catch(() => {});
    axios.get(`${API}/locations`).then(r => setLocations(r.data)).catch(() => {});
  }, [user]);

  const save = async (data, files = [], removed = []) => {
    try {
      const aid = editing?.id ? (await axios.put(`${API}/user-announcements/${editing.id}`, data)).data.id : (await axios.post(`${API}/user-announcements`, data)).data.id;
      for (const attId of removed) await axios.delete(`${API}/user-announcements/${aid}/attachments/${attId}`);
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        try { await axios.post(`${API}/user-announcements/${aid}/attachments`, fd); } catch (e) { toast.error(`${file.name}: ${errText(e)}`); }
      }
      toast.success('Duyuru kaydedildi');
      setEditing(null);
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const toggle = async (a, v) => { try { await axios.put(`${API}/user-announcements/${a.id}`, strip({ ...a, is_active: v })); load(); } catch (e) { toast.error(errText(e)); } };
  const remove = async (a) => {
    if (!(await confirmDialog({ title: 'Duyuru silinsin mi?', description: `"${a.title}" ve okunma kayıtları silinecek.`, confirmLabel: 'Evet, Sil' }))) return;
    try { await axios.delete(`${API}/user-announcements/${a.id}`); toast.success('Duyuru silindi'); load(); } catch (e) { toast.error(errText(e)); }
  };

  if (!canView('user_announcements')) return <p className="text-slate-500">Bu sayfa sadece Süper Admin kullanıcılar içindir.</p>;
  return (
    <div className="space-y-6 max-w-5xl" data-testid="user-announcements-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><BellRing className="h-6 w-6 text-rose-500" />Kullanıcı Duyuruları</h1>
          <p className="text-slate-500 mt-1 max-w-2xl">Firma kullanıcıları giriş yaptığında açılır pencere olarak çıkar. Firma statüsü ve lokasyona göre hedefleyin; her duyurunun altında hangi firmaların gördüğünü, onayladığını veya henüz görmediğini takip edin.</p>
        </div>
        <Button onClick={() => setEditing({})} className="bg-slate-900 hover:bg-slate-800" data-testid="ua-add-btn"><Plus className="h-4 w-4 mr-1" />Yeni Duyuru</Button>
      </div>
      {items.length === 0
        ? <Card className="border-dashed"><CardContent className="p-10 text-center text-slate-500" data-testid="ua-empty">Henüz duyuru yok.</CardContent></Card>
        : <div className="space-y-3" data-testid="ua-list">{items.map(a => <Item key={a.id} a={a} statuses={statuses} locations={locations} onEdit={setEditing} onDelete={remove} onToggle={toggle} />)}</div>}
      <UserAnnouncementFormDialog open={!!editing} item={editing} statuses={statuses} locations={locations} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
}
